/**
 * rentals — RENTALS, the pure rules (the interiors programme, slice R, 2026-10-01). "add rentals feature too" /
 * "no new currencies" — a room an owner lets, a room a traveler takes.
 *
 * BYTE-IDENTICAL at client/src/lib/rentals.ts and server/src/lib/rental_rules.ts (`verify:rentals` diffs them): the
 * client shows exactly what the server will charge, refuse and release. No imports — both sides feed it their numbers.
 *
 * WHAT A LEASE IS. Rent is SOV (the existing credits) by WHOLE DAYS. A day's rent becomes the landlord's when that day
 * STARTS: day 1 is paid through at signing; every other prepaid day waits in ONE escrow wallet (`RENT_ESCROW_BITS`)
 * and is released at its own start. So what is refundable is exactly what the escrow still holds — a refund never
 * depends on what the landlord has left. The fee (`RENT_FEE_BPS`, the market tax's rate) is a SINK to the platform,
 * taken from each day as it is released. Clocks: `since` never moves; `paidDays` and `releasedDays` only rise
 * (NEVER MOVE AN ACCRUAL CLOCK BACKWARDS); `paidUntil = since + paidDays × DAY`.
 *
 * ┌───────────────────────────────────────────────────────────────────────────────────────────────────────────────┐
 * │ THE OWNER'S DIAL — `lapseDecision` (below) is the ONE function that decides what happens when rent runs out.    │
 * │ It is small on purpose: change it here AND in the twin file (the harness fails until both are the same bytes),  │
 * │ re-pin its table in client/verify/rentals.mts §3, publish.                                                     │
 * └───────────────────────────────────────────────────────────────────────────────────────────────────────────────┘
 */

// ─── Constants ───────────────────────────────────────────────────────────────────────────────────────────────────────

/** One rent period. */
export const DAY_S = 86_400;
/** The rollout flag (ABSENT = ON; registered in server/src/reducers/platform.ts KNOWN_FLAGS). OFF: nothing can be
 *  listed, rented or extended; leases already signed run out on their own terms and the sweep still returns goods. */
export const RENTALS_FLAG = 'interior_rentals_v1';

/** Rent bounds, SOV a day. */
export const RENT_MIN_SOV = 1;
export const RENT_MAX_SOV = 50_000;
/** How far ahead of NOW a tenant may be paid up (days). */
export const RENT_HORIZON_DAYS = 7;
/** The fee on every day's rent — a sink, the market tax's rate (server `MARKET_TAX_BPS` 500 = 5 %). */
export const RENT_FEE_BPS = 500;
/** The notice a landlord must give: an eviction takes effect at the first lease-day boundary at least this long after
 *  the notice — never inside a day the tenant has paid for. */
export const EVICT_NOTICE_S = 24 * 3600;
/** The default grace after `paidUntil` (see `lapseDecision`). */
export const LAPSE_GRACE_S = 24 * 3600;

/** The escrow wallet's identity bits (the server wraps them: `new Identity(RENT_ESCROW_BITS)`). The banks' vaults are
 *  0xba4c…, the guild vaults 0x6a11…, the operations' escrows 0x0e5c…. */
export const RENT_ESCROW_BITS = 0x7e47_0000_0000n;
/** Ledger reasons — NEVER `rental` (the Creator Exchange's world-rental revenue, which `settleDaily` selects by reason). */
export const RENT_REASON = { rent: 'room_rent', fee: 'room_rent_fee', escrow: 'room_escrow', refund: 'room_refund' } as const;

/** Units one structure may list, keyed pieces one unit may name, an owner's note. */
export const UNITS_PER_STRUCTURE = 24;
export const UNIT_KEYS_MAX = 24;
export const UNIT_NOTE_MAX = 80;
/** Rooms one traveler may rent at once. */
export const TENANT_LEASES_MAX = 4;
/** A unit's floor area (m²): a room, not a cupboard and not a whole hall. */
export const UNIT_AREA_MIN_M2 = 2;
export const UNIT_AREA_MAX_M2 = 400;
/** How far a unit's threshold may stand from its rect (m). */
export const UNIT_THRESHOLD_REACH_M = 4;
/** How far the interior's origin may sit from the structure's (m) — the model's bbox centre against its pivot. */
export const UNIT_ORIGIN_MAX_M = 6;
/** The kinds a planner unit comes in (interior_descriptor.ts `DescUnit.kind`). */
export const UNIT_KINDS: readonly string[] = ['room', 'dwelling', 'spare'];

/** MIRRORS of the shell's own numbers (client `lib/building_interiors.ts`; `verify:rentals` pins them against
 *  `interiorSpec` for every deed type): the storey pitch, the floor cap, the wall inset, the 60 m core. */
export const SHELL_FLOOR_H = 3.4;
export const SHELL_MAX_FLOORS = 8;
export const SHELL_WALL_IN = 0.45;
export const SHELL_CORE_M = 60;
/** MIRROR of client `lib/furniture_place.ts FURNITURE_RENDER_SCALE`: what a catalog box is drawn at. */
export const DRAWN_SCALE = 1.5;

/** THE KEEP-OUT (a tenant is never boxed in): while a unit is LET nobody may stand furniture within this distance of
 *  the walk from its threshold's standing point to its door point — plus the piece's own drawn radius. */
export const THRESHOLD_KEEPOUT_M = 0.9;

// ─── THE OWNER'S DIAL ────────────────────────────────────────────────────────────────────────────────────────────────

export type LapsePhase = 'paid' | 'grace' | 'release';
/** What the sweep knows about a lease when it asks (every field optional — the default reads none of them). */
export interface LapseFacts {
  /** The lease's rent, SOV a day. */
  rentPerDay?: number;
  /** Entries the tenant has stored in the unit. */
  stored?: number;
  /** The landlord's notice has been given (the eviction boundary then ends the lease, not this function). */
  underNotice?: boolean;
}
export interface LapseVerdict {
  /** `paid` — nothing to do. `grace` — the rent has run out; the tenant keeps the room and may still pay (a late
   *  payment starts at `paidUntil`, so the grace is never a free day for a tenant who stays). `release` — the lease
   *  ends NOW: the unit is free to let again. */
  phase: LapsePhase;
  /** When `grace` turns into `release` (unix seconds). */
  releaseAtS: number;
  /** Where the tenant's stored goods go at `release`. The server implements 'tenant' — THE RETURN LAW: back to the
   *  tenant's own inventory, never deleted, never the landlord's. */
  goods: 'tenant';
}
/**
 * WHAT HAPPENS WHEN RENT RUNS OUT. The default (the brief's): a 24 h grace after `paidUntil`, then the unit is
 * released and everything the tenant stored is returned to the tenant.
 *
 * Alternatives the owner may choose — each is a few lines:
 *   · A LONGER (or no) GRACE — change `LAPSE_GRACE_S` (0 = the room is released the moment the rent runs out). The
 *     grace costs the landlord at most that much unpaid time from a tenant who never comes back.
 *   · A GRACE THAT GROWS WITH WHAT IS STORED — `releaseAtS = paidUntilS + LAPSE_GRACE_S + min(3, facts.stored ?? 0) * DAY_S`.
 *   · AUTOPAY FROM A BANK ACCOUNT — not a change here: the sweep settles what is paid BEFORE it asks this function
 *     (AUTOPAY BEFORE LATENESS), so an autopay step belongs in server `lib/rental_ops.ts rentalSweep`, just before
 *     the call — debit the tenant's Reserve account (`bank_ops.bookDebit` + a vault → escrow posting) for one more
 *     day and let this function see the new `paidUntil`.
 *   · GOODS HELD AT THE OWNER'S CONSOLE (a lien until the arrears are paid) — return `goods: 'console'` here, widen the
 *     type, and give `rental_ops.endLease` a branch that leaves the `rental_stored` rows in place under a lease state
 *     `held` instead of calling `returnTenantGoods`. Until that branch exists the server returns goods to the tenant
 *     whatever this says — a lien is a rule the owner must choose on purpose.
 */
export function lapseDecision(nowS: number, paidUntilS: number, _facts: LapseFacts = {}): LapseVerdict {
  const releaseAtS = paidUntilS + LAPSE_GRACE_S;
  if (nowS < paidUntilS) return { phase: 'paid', releaseAtS, goods: 'tenant' };
  if (nowS < releaseAtS) return { phase: 'grace', releaseAtS, goods: 'tenant' };
  return { phase: 'release', releaseAtS, goods: 'tenant' };
}

// ─── Money ───────────────────────────────────────────────────────────────────────────────────────────────────────────

/** The fee on a gross amount (whole SOV, floored — a rent under 20 a day pays no fee; the leg is then omitted). */
export function rentFee(gross: number): number { return Math.floor((gross * RENT_FEE_BPS) / 10_000); }

/** Why a rent is not a rent ('' = fine). */
export function rentProblem(rentPerDay: number): string {
  if (!Number.isInteger(rentPerDay)) return 'Rent is a whole number of credits a day';
  if (rentPerDay < RENT_MIN_SOV || rentPerDay > RENT_MAX_SOV) return `Rent is ${RENT_MIN_SOV}–${RENT_MAX_SOV} credits a day`;
  return '';
}

export interface RentSplit { gross: number; fee: number; toOwner: number }
/** `days` days of rent released to the landlord at once: the fee is taken PER DAY, so n days released together cost
 *  exactly what n days released one by one do. */
export function releaseSplit(rentPerDay: number, days: number): RentSplit {
  const fee = rentFee(rentPerDay) * days, gross = rentPerDay * days;
  return { gross, fee, toOwner: gross - fee };
}

export interface RentQuote {
  days: number;
  /** What leaves the tenant's wallet. */
  total: number;
  /** Day 1, paid through at signing. */
  first: RentSplit;
  /** What waits in escrow: the other days. */
  escrow: number;
}
/** What `rentUnit` moves for `days` days at `rentPerDay`. total = first.toOwner + first.fee + escrow, exactly. */
export function rentQuote(rentPerDay: number, days: number): RentQuote {
  return { days, total: rentPerDay * days, first: releaseSplit(rentPerDay, 1), escrow: rentPerDay * (days - 1) };
}

// ─── The lease's clocks ──────────────────────────────────────────────────────────────────────────────────────────────

export interface LeaseClock { sinceS: number; paidDays: number; releasedDays: number; rentPerDay: number; noticeEndS?: number }

export function paidUntilS(sinceS: number, paidDays: number): number { return sinceS + paidDays * DAY_S; }
/** How many lease days have STARTED by `nowS` (day 1 starts at `since`). */
export function daysStarted(sinceS: number, nowS: number): number {
  return nowS < sinceS ? 0 : Math.floor((nowS - sinceS) / DAY_S) + 1;
}
/** The days whose rent is due to the landlord now and not yet released (≥ 0; never more than was paid). `untilS` caps
 *  the count at a boundary the lease ended on (an eviction): a day that never started is never released. */
export function daysToRelease(c: LeaseClock, nowS: number, untilS = 0): number {
  const at = untilS > 0 && untilS <= nowS ? untilS - 1 : nowS; // a lease that ended AT a boundary did not start the next day
  return Math.max(0, Math.min(c.paidDays, daysStarted(c.sinceS, at)) - c.releasedDays);
}
/** What the escrow holds for a lease: the days paid for and not yet released. */
export function heldFor(c: Pick<LeaseClock, 'paidDays' | 'releasedDays' | 'rentPerDay'>): number {
  return Math.max(0, c.paidDays - c.releasedDays) * c.rentPerDay;
}
/** What a tenant would get back by leaving at `nowS` (the unstarted days; the day in progress is the landlord's). */
export function refundAt(c: LeaseClock, nowS: number): number {
  const released = c.releasedDays + daysToRelease(c, nowS);
  return Math.max(0, c.paidDays - released) * c.rentPerDay;
}
/** The boundary a notice given at `noticeAtS` ends the lease at: the first lease-day boundary ≥ notice + 24 h. */
export function noticeEndS(sinceS: number, noticeAtS: number): number {
  const k = Math.max(1, Math.ceil((noticeAtS + EVICT_NOTICE_S - sinceS) / DAY_S));
  return sinceS + k * DAY_S;
}
/** How many MORE days a tenant may buy at `nowS`: never past the horizon, never past a notice's boundary. */
export function maxExtraDays(c: LeaseClock, nowS: number): number {
  const pu = paidUntilS(c.sinceS, c.paidDays);
  let d = Math.floor((nowS + RENT_HORIZON_DAYS * DAY_S - pu) / DAY_S);
  if (c.noticeEndS && c.noticeEndS > 0) d = Math.min(d, Math.floor((c.noticeEndS - pu) / DAY_S));
  return Math.max(0, d);
}
/** How many days a NEW lease may be signed for. */
export function maxFirstDays(): number { return RENT_HORIZON_DAYS; }

// ─── The unit (what an owner claims, what the server bounds) ─────────────────────────────────────────────────────────

export const UNIT_ID_RE = /^unit\/f(\d{1,2})\/(\d{1,2})$/;
/** `pack:<floor>:<n>:<furnitureKey>` — the server's canonical pack key (server reducers/furniture.ts PACK_KEY). */
export const UNIT_PACK_KEY_RE = /^pack:(\d{1,2}):(\d{1,3}):([a-z][a-z0-9_]{1,40})$/;
export const UNIT_DIGEST_RE = /^[0-9a-f]{32}$/;

export function parseUnitId(unitId: string): { floor: number; n: number } | null {
  const m = UNIT_ID_RE.exec(unitId);
  return m ? { floor: Number(m[1]), n: Number(m[2]) } : null;
}
/** The storeys a shell of height `h` has — `interiorSpec`'s own count. */
export function shellFloors(h: number): number {
  return Math.max(1, Math.min(SHELL_MAX_FLOORS, Math.floor((h - 1.6) / SHELL_FLOOR_H)));
}
/** The walkable core's half extents — `interiorSpec`'s `hw` / `hd`. */
export function shellCore(w: number, d: number): { hw: number; hd: number } {
  return { hw: Math.min(w, SHELL_CORE_M) / 2 - SHELL_WALL_IN, hd: Math.min(d, SHELL_CORE_M) / 2 - SHELL_WALL_IN };
}
/** The storey a height above the structure's seat belongs to — a MIRROR of client `interior_wiring.floorOfHeight`. */
export function floorOfHeight(ly: number, floors: number): number {
  return Math.max(0, Math.min(Math.max(1, floors) - 1, Math.floor((ly + 0.3) / SHELL_FLOOR_H)));
}

export interface UnitRect { floor: number; x0: number; z0: number; x1: number; z1: number }
export interface UnitGeom extends UnitRect {
  /** The threshold: the door point, and where a body stands to use it (the interior's frame). */
  tx: number; tz: number; ax: number; az: number;
  /** The interior's origin in the STRUCTURE's own frame (the frame `structure_item.lx / lz` use): the model's footprint
   *  centre against its pivot. A point of the structure frame is in the interior's at (lx − ox, lz − oz). */
  ox: number; oz: number;
}
export interface UnitClaim extends UnitGeom { unitId: string; kind: string; keys: string[] }

/**
 * WHY A CLAIM IS REFUSED ('' = it is a unit this shell could hold). The server has no planner of its own yet (slice G
 * mirrors it this wave): the owner's client names the unit and the server BOUNDS what it names. A lying owner can only
 * mis-describe a room of their own building.
 */
export function unitClaimProblem(c: UnitClaim, shell: { w: number; d: number; h: number }): string {
  const id = parseUnitId(c.unitId);
  if (!id) return 'Not a unit of this building';
  if (!UNIT_KINDS.includes(c.kind)) return 'Unknown kind of unit';
  for (const v of [c.x0, c.z0, c.x1, c.z1, c.tx, c.tz, c.ax, c.az, c.ox, c.oz]) if (!Number.isFinite(v)) return 'Numbers must be finite';
  if (!Number.isInteger(c.floor) || c.floor !== id.floor) return 'The unit is not on that floor';
  if (c.floor < 0 || c.floor >= shellFloors(shell.h)) return 'This building has no such floor';
  const { hw, hd } = shellCore(shell.w, shell.d);
  const pad = 0.6; // a rect may reach the wall band
  if (!(c.x0 < c.x1 && c.z0 < c.z1)) return 'The unit has no floor';
  if (c.x0 < -hw - pad || c.x1 > hw + pad || c.z0 < -hd - pad || c.z1 > hd + pad) return 'The unit lies outside the building';
  const area = (c.x1 - c.x0) * (c.z1 - c.z0);
  if (area < UNIT_AREA_MIN_M2 || area > UNIT_AREA_MAX_M2) return 'That is not a room';
  for (const [x, z] of [[c.tx, c.tz], [c.ax, c.az]] as const) {
    if (Math.abs(x) > hw + pad || Math.abs(z) > hd + pad) return 'The unit\'s door lies outside the building';
    const dx = Math.max(c.x0 - x, 0, x - c.x1), dz = Math.max(c.z0 - z, 0, z - c.z1);
    if (Math.hypot(dx, dz) > UNIT_THRESHOLD_REACH_M) return 'The unit\'s door is not at the unit';
  }
  if (Math.abs(c.ox) > UNIT_ORIGIN_MAX_M || Math.abs(c.oz) > UNIT_ORIGIN_MAX_M) return 'The unit is not in this building\'s frame';
  if (c.keys.length > UNIT_KEYS_MAX) return `A unit names at most ${UNIT_KEYS_MAX} pieces`;
  const seen = new Set<string>();
  for (const k of c.keys) {
    const m = UNIT_PACK_KEY_RE.exec(k);
    if (!m) return 'A unit\'s pieces are furnish-pack pieces';
    if (Number(m[1]) !== c.floor) return 'A unit\'s pieces stand on the unit\'s floor';
    if (`pack:${Number(m[1])}:${Number(m[2])}:${m[3]}` !== k) return 'A piece key must be canonical';
    if (seen.has(k)) return 'A piece is named twice';
    seen.add(k);
  }
  return '';
}

/** Two units of one floor share floor area (touching edges do not). */
export function unitsOverlap(a: UnitRect, b: UnitRect): boolean {
  return a.floor === b.floor && a.x0 < b.x1 - 1e-3 && b.x0 < a.x1 - 1e-3 && a.z0 < b.z1 - 1e-3 && b.z0 < a.z1 - 1e-3;
}
/** Does a point of the INTERIOR's frame lie in the unit (grown by `pad`)? */
export function unitHolds(u: UnitRect, floor: number, x: number, z: number, pad = 0): boolean {
  return floor === u.floor && x >= u.x0 - pad && x <= u.x1 + pad && z >= u.z0 - pad && z <= u.z1 + pad;
}
/** Does a point of the STRUCTURE's frame (a `structure_item`'s lx / ly / lz) lie in the unit? */
export function unitHoldsItem(u: UnitGeom, floors: number, lx: number, ly: number, lz: number, pad = 0): boolean {
  return unitHolds(u, floorOfHeight(ly, floors), lx - u.ox, lz - u.oz, pad);
}
/** The drawn radius of a catalog piece `w × d` (its longer drawn half side); a piece the catalog does not know: 0.6 m. */
export function drawnRadius(w?: number, d?: number): number {
  return w !== undefined && d !== undefined ? (Math.max(w, d) * DRAWN_SCALE) / 2 : 0.6;
}
/**
 * THE KEEP-OUT under a lease: 'inside' — the piece would stand in the let room (it is the tenant's alone);
 * 'threshold' — it would stand on the walk from the threshold's standing point to its door point. '' = clear.
 * `x, z` are in the INTERIOR's frame; `r` the piece's drawn radius.
 */
export function unitKeepOut(u: UnitGeom, floor: number, x: number, z: number, r: number): '' | 'inside' | 'threshold' {
  if (floor !== u.floor) return '';
  if (unitHolds(u, floor, x, z, r)) return 'inside';
  const vx = u.tx - u.ax, vz = u.tz - u.az, len2 = vx * vx + vz * vz;
  const t = len2 > 1e-9 ? Math.max(0, Math.min(1, ((x - u.ax) * vx + (z - u.az) * vz) / len2)) : 0;
  const d = Math.hypot(x - (u.ax + vx * t), z - (u.az + vz * t));
  return d < THRESHOLD_KEEPOUT_M + r ? 'threshold' : '';
}

// ─── Words (one wording on both sides) ───────────────────────────────────────────────────────────────────────────────

/** An owner's note: one line, no control characters, at most `UNIT_NOTE_MAX`. */
export function cleanNote(text: string): string {
  let out = '';
  for (const ch of text) { const c = ch.codePointAt(0) ?? 0; out += c < 32 || c === 127 ? ' ' : ch; }
  return out.replace(/\s+/g, ' ').trim().slice(0, UNIT_NOTE_MAX);
}
/** `2 d 3 h`, `5 h`, `12 min` — what a paid-until or a grace reads as. */
export function spanLabel(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const d = Math.floor(s / DAY_S), h = Math.floor((s % DAY_S) / 3600), m = Math.floor((s % 3600) / 60);
  if (d > 0) return h > 0 ? `${d} d ${h} h` : `${d} d`;
  if (h > 0) return `${h} h`;
  return `${Math.max(1, m)} min`;
}
